import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { FormsModule } from '@angular/forms';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  UntypedFormArray,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatRadioModule } from '@angular/material/radio';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatStepperModule } from '@angular/material/stepper';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgClass, NgStyle, CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { OnlineInscriptionService } from './online-inscription.service';
import {
  SchoolSearchResult,
  SchoolLevel,
  OnlineInscriptionRequest,
  OnlineInscriptionResponse,
} from './inscription.model';

@Component({
  selector: 'app-first-inscription',
  templateUrl: './inscription.component.html',
  styleUrls: ['./inscription.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatToolbarModule,
    MatButtonModule,
    RouterLink,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    ReactiveFormsModule,
    MatIconModule,
    MatCheckboxModule,
    MatRadioModule,
    MatDatepickerModule,
    MatStepperModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatSnackBarModule,
    MatTooltipModule,
    TranslateModule,
    NgClass,
    NgStyle,
  ],
})
export class InscLayoutComponent implements OnInit, OnDestroy {
  currentYear = new Date().getFullYear();
  currentStep = 0;
  totalSteps = 4;

  // Max birth date is today (children cannot be born in the future)
  maxBirthDate = new Date();

  // Step 1: School search
  searchTerm = '';
  filterType = '';
  filterCity = '';
  filterRegion = '';
  schoolTypes: string[] = [];
  searchResults: SchoolSearchResult[] = [];
  selectedSchool: SchoolSearchResult | null = null;
  schoolLevels: SchoolLevel[] = [];
  isSearching = false;

  // Step 2: Parent form
  parentForm!: UntypedFormGroup;

  // Step 3: Children form
  childrenForm!: UntypedFormGroup;

  // Step 4: Confirmation
  acceptTerms = false;
  isSubmitting = false;
  submissionSuccess = false;
  submittedEmail = '';

  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(
    private fb: UntypedFormBuilder,
    private inscriptionService: OnlineInscriptionService,
    private snackBar: MatSnackBar,
    private router: Router,
    private translate: TranslateService,
  ) {}

  get logoSrc(): string {
    const lang = this.translate.currentLang || localStorage.getItem('lang') || 'fr';
    return lang === 'ar'
      ? 'assets/images/logos/logo-gardKids-ar-png.png'
      : 'assets/images/logos/logo-gardKids-fr-png.png';
  }

  ngOnInit(): void {
    this.buildForms();

    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((term) => {
        this.runSchoolSearch(term);
      });

    // Load all schools on init
    this.searchSubject.next('');
  }

  private runSchoolSearch(term: string = this.searchTerm): void {
    this.isSearching = true;
    this.inscriptionService
      .searchSchools(term, {
        type: this.filterType,
        city: this.filterCity,
        region: this.filterRegion,
      })
      .subscribe((results) => {
        this.searchResults = results;
        const types = new Set(results.map((r) => r.type).filter(Boolean));
        // Keep known types even when filtered empty
        this.schoolTypes = Array.from(new Set([...this.schoolTypes, ...types])).sort();
        this.isSearching = false;
      });
  }

  onFilterChange(): void {
    this.runSchoolSearch(this.searchTerm);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearchChange(value: string): void {
    this.searchTerm = value;
    this.searchSubject.next(value);
  }

  selectSchool(school: SchoolSearchResult): void {
    this.selectedSchool = school;
    this.schoolLevels = [];
    this.inscriptionService.getSchoolLevels(school.id).subscribe({
      next: (levels) => {
        this.schoolLevels = levels;
        this.currentStep = 1;
      },
      error: () => {
        this.currentStep = 1;
      }
    });
  }

  changeSchool(): void {
    this.selectedSchool = null;
    this.schoolLevels = [];
    this.currentStep = 0;
  }

  // Children FormArray
  get childrenArray(): UntypedFormArray {
    return this.childrenForm.get('children') as UntypedFormArray;
  }

  addChild(): void {
    this.childrenArray.push(this.createChildGroup());
  }

  removeChild(index: number): void {
    if (this.childrenArray.length > 1) {
      this.childrenArray.removeAt(index);
    }
  }

  // Navigation
  nextStep(): void {
    if (this.currentStep === 1 && this.parentForm.invalid) {
      this.parentForm.markAllAsTouched();
      return;
    }
    if (this.currentStep === 2 && this.childrenForm.invalid) {
      this.childrenForm.markAllAsTouched();
      return;
    }
    if (this.currentStep < this.totalSteps - 1) {
      this.currentStep++;
    }
  }

  prevStep(): void {
    if (this.currentStep > 0) {
      this.currentStep--;
    }
  }

  goToStep(step: number): void {
    // Only allow going back, or forward if previous steps valid
    if (step < this.currentStep) {
      this.currentStep = step;
    }
  }

  // Submit
  onSubmit(): void {
    if (!this.acceptTerms || !this.selectedSchool) return;

    this.isSubmitting = true;
    const parentVal = this.parentForm.value;
    const childrenVal = this.childrenArray.value;
    const addressParts = [parentVal.region, parentVal.city, parentVal.address]
      .map((p: string) => (p || '').trim())
      .filter(Boolean);

    const request: OnlineInscriptionRequest = {
      school_id: this.selectedSchool.id,
      parent: {
        first_name: parentVal.firstName,
        last_name: parentVal.lastName,
        email: parentVal.email,
        phone: parentVal.phoneNumber,
        second_phone: parentVal.secondPhoneNumber,
        civil_state: parentVal.civilState,
        region: parentVal.region || undefined,
        city: parentVal.city || undefined,
        address: addressParts.join(', ') || parentVal.address,
      },
      children: childrenVal.map((c: Record<string, string | number | Date>) => {
        let bd = c['birthDate'];
        if (bd instanceof Date) {
          bd = bd.toISOString().split('T')[0];
        } else if (typeof bd === 'string' && bd.includes('T')) {
          bd = bd.split('T')[0];
        }
        return {
          first_name: c['firstName'],
          last_name: c['lastName'],
          birth_date: bd,
          gender: c['gender'],
          level_id: c['levelId'],
        };
      }),
    };

    this.inscriptionService.submitInscription(request).subscribe({
      next: (responses: OnlineInscriptionResponse[]) => {
        this.isSubmitting = false;
        this.submissionSuccess = true;
        this.submittedEmail = parentVal.email;
        this.currentStep = 4;
      },
      error: (err) => {
        this.isSubmitting = false;
        const message = typeof err === 'string' ? err : (err?.error?.message || err?.message || 'An error occurred. Please try again.');
        this.snackBar.open(message, '', {
          duration: 5000,
          panelClass: 'snackbar-danger',
        });
      },
    });
  }

  getSelectedLevelName(levelId: number): string {
    const level = this.schoolLevels.find((l) => l.id === levelId);
    return level ? level.level_name : '';
  }

  private buildForms(): void {
    this.parentForm = this.fb.group({
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      phoneNumber: ['+216', [Validators.required, Validators.pattern(/^\+216\d{8}$/)]],
      secondPhoneNumber: ['', [Validators.pattern(/^\+216\d{8}$/)]],
      civilState: [''],
      region: [''],
      city: [''],
      address: [''],
    });

    this.childrenForm = this.fb.group({
      children: this.fb.array([this.createChildGroup()]),
    });
  }

  private createChildGroup(): UntypedFormGroup {
    return this.fb.group({
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      birthDate: ['', [Validators.required]],
      gender: ['', [Validators.required]],
      levelId: ['', [Validators.required]],
      remarks: [''],
    });
  }
}
